import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { logAnalyticsPageView } from './analytics';
import { detectVisitorCountry } from './detectVisitorCountry';
import { readClientDevice } from './deviceClass';

function resolveClientCountry(
  userCountry: string | undefined,
  userType: string | undefined,
  investorCountry: string | undefined,
  startupCountry: string | undefined
): string | undefined {
  const fromUser = userCountry?.trim();
  if (fromUser) return fromUser;
  if (userType === 'investor') {
    const fromProfile = investorCountry?.trim();
    if (fromProfile) return fromProfile;
  }
  if (userType === 'startup') {
    const fromProfile = startupCountry?.trim();
    if (fromProfile) return fromProfile;
  }
  return undefined;
}

/** Log each route change to Firestore (pageViews) and Firebase Analytics when consented. */
export function usePageViewTracking() {
  const location = useLocation();
  const { user } = useAuth();
  const { investorProfile, startupProfile } = useProfile();

  useEffect(() => {
    let cancelled = false;
    const pathname = location.pathname;
    const search = location.search || undefined;
    const pagePath = `${pathname}${location.search}`;

    void logAnalyticsPageView(pagePath);

    const trackPageView = async () => {
      const loggedInCountry = resolveClientCountry(
        user?.country,
        user?.userType,
        investorProfile?.country,
        startupProfile?.country
      );
      const userCountry = loggedInCountry ?? (await detectVisitorCountry());
      if (cancelled) return;

      try {
        const trackPageViewFn = httpsCallable(functions, 'trackPageView');
        const { device, viewportWidth } = readClientDevice();
        await trackPageViewFn({
          pathname,
          search,
          referrer: document.referrer || undefined,
          device,
          viewportWidth,
          ...(userCountry ? { userCountry } : {}),
        });
      } catch (error) {
        console.error('[PageView] Failed to track page view:', error);
      }
    };

    void trackPageView();
    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    user?.id,
    user?.country,
    user?.userType,
    investorProfile?.country,
    startupProfile?.country,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    const ping = (active: boolean) => {
      const trackPageViewFn = httpsCallable(functions, 'trackPageView');
      void trackPageViewFn({
        presenceOnly: true,
        active,
        pathname: window.location.pathname,
      }).catch(() => undefined);
    };
    const pingIfVisible = () => ping(document.visibilityState !== 'hidden');
    pingIfVisible();
    const intervalId = window.setInterval(pingIfVisible, 20_000);
    document.addEventListener('visibilitychange', pingIfVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', pingIfVisible);
      ping(false);
    };
  }, [user?.id]);
}
